import {
  CONSENT_COOKIE_NAME,
  CONSENT_MAX_AGE_DAYS,
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION,
} from './constants';
import {
  defaultConsentState,
  type ConsentChoices,
  type ConsentState,
} from './types';

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function parseConsentState(raw: string | null | undefined): ConsentState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof parsed.version !== 'number' ||
      typeof parsed.hasResponded !== 'boolean' ||
      typeof parsed.choices !== 'object' ||
      parsed.choices === null
    ) {
      return null;
    }

    if (parsed.version !== CONSENT_VERSION) {
      return null;
    }

    return {
      version: CONSENT_VERSION,
      updatedAt:
        typeof parsed.updatedAt === 'string'
          ? parsed.updatedAt
          : new Date().toISOString(),
      hasResponded: parsed.hasResponded,
      choices: {
        necessary: true,
        externalContent: Boolean(parsed.choices.externalContent),
      },
    };
  } catch {
    return null;
  }
}

function readCookie(name: string): string | null {
  if (!isBrowser()) return null;
  const match = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));
  if (!match) return null;
  return decodeURIComponent(match.slice(name.length + 1));
}

function writeCookie(name: string, value: string, maxAgeDays: number): void {
  if (!isBrowser()) return;
  const maxAge = maxAgeDays * 24 * 60 * 60;
  const secure =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? '; Secure'
      : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

export function readConsentState(): ConsentState {
  if (!isBrowser()) {
    return defaultConsentState();
  }

  const fromStorage = parseConsentState(
    window.localStorage.getItem(CONSENT_STORAGE_KEY),
  );
  if (fromStorage?.hasResponded) {
    return fromStorage;
  }

  const fromCookie = parseConsentState(readCookie(CONSENT_COOKIE_NAME));
  if (fromCookie?.hasResponded) {
    try {
      window.localStorage.setItem(
        CONSENT_STORAGE_KEY,
        JSON.stringify(fromCookie),
      );
    } catch {
      // ignore quota / private mode
    }
    return fromCookie;
  }

  return defaultConsentState();
}

export function writeConsentState(choices: ConsentChoices): ConsentState {
  const state: ConsentState = {
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
    hasResponded: true,
    choices: {
      necessary: true,
      externalContent: Boolean(choices.externalContent),
    },
  };

  if (!isBrowser()) {
    return state;
  }

  const serialized = JSON.stringify(state);
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, serialized);
  } catch {
    // ignore
  }
  writeCookie(CONSENT_COOKIE_NAME, serialized, CONSENT_MAX_AGE_DAYS);
  return state;
}
